import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Recommendation } from '../../types';
import { formatDateWithYear } from '../../utils/format';
import Modal from '../ui/Modal';

interface OpenPositionModalProps {
  recommendation: Recommendation;
  agentName?: string;
  agentPositionId?: string;
  onClose: () => void;
  onConfirm: (data: {
    recommendation_id: string;
    symbol: string;
    strategy: string;
    strike: number;
    expiration: string;
    option_symbol: string;
    contracts: number;
    entry_premium: number;
    price_at_entry: number;
    agent_position_id?: string;
    stock_entry_price?: number;
    current_stock_price?: number;
    cost_basis?: number;
  }) => Promise<void>;
}

const OpenPositionModal: React.FC<OpenPositionModalProps> = ({
  recommendation,
  agentName,
  agentPositionId,
  onClose,
  onConfirm
}) => {
  const [contracts, setContracts] = useState(1);
  const [entryPremium, setEntryPremium] = useState(recommendation.premium);
  const [stockEntryPrice, setStockEntryPrice] = useState(
    recommendation.stock_entry_price || recommendation.price_at_rec || 0
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const isShares = recommendation.strategy === 'SHARES';
  const isCoveredCall = recommendation.strategy.includes('Call');
  const needsStockLeg = isShares || isCoveredCall;

  const stockBasis = stockEntryPrice || recommendation.stock_entry_price || recommendation.price_at_rec || 0;
  const optionCollateral = recommendation.strike * 100 * contracts;
  const sharesCollateral = stockBasis * 100 * contracts;

  const collateral = needsStockLeg ? sharesCollateral : optionCollateral;
  const maxProfit = entryPremium * 100 * contracts;
  const targetReturn = !isShares && recommendation.strike > 0
    ? ((entryPremium / recommendation.strike) * 100).toFixed(2)
    : '0.00';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Use the server-provided recommendation ID for proper filtering
      const recId = recommendation.id || `rec_${recommendation.symbol}_${recommendation.strike}_${recommendation.expiration?.replace(/-/g, '')}`;
      
      await onConfirm({
        recommendation_id: recId,
        symbol: recommendation.symbol,
        strategy: recommendation.strategy,
        strike: recommendation.strike,
        expiration: recommendation.expiration || '',
        option_symbol: recommendation.option_symbol || '',
        contracts,
        entry_premium: isShares ? 0 : entryPremium,
        price_at_entry: isShares ? stockBasis : (recommendation.price_at_rec || stockBasis || 0),
        agent_position_id: agentPositionId,
        stock_entry_price: needsStockLeg ? stockBasis : recommendation.stock_entry_price,
        current_stock_price: recommendation.current_stock_price,
        cost_basis: recommendation.cost_basis,
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open position');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-white">{agentName ? 'Take Agent Trade' : 'Open Position'}</h2>
            <p className="text-sm text-slate-400">
              {recommendation.symbol} • {recommendation.strategy}
              {agentName && <span className="ml-1 text-cyan-400">• {agentName}</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Position Opened!</h3>
            <p className="text-slate-400">Your position has been recorded.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Contract Details */}
            <div className="p-4 space-y-4">
              {/* Trade Summary */}
              <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Strike Price</span>
                  <span className="text-white font-medium">${recommendation.strike}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Expiration</span>
                  <span className="text-white font-medium">{formatDateWithYear(recommendation.expiration)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">AI Suggested Premium</span>
                  <span className="text-green-400 font-medium">${recommendation.premium.toFixed(2)}</span>
                </div>
                {needsStockLeg ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Stock Basis</span>
                    <span className="text-white font-medium">${stockBasis.toFixed(2)}</span>
                  </div>
                ) : null}
              </div>

              {/* Contracts Input */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Number of Contracts
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={contracts}
                  onChange={(e) => setContracts(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                />
              </div>

              {!isShares ? (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    Your Fill Premium
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={entryPremium}
                      onChange={(e) => setEntryPremium(parseFloat(e.target.value) || 0)}
                      className="w-full pl-7 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Enter your actual option premium fill</p>
                </div>
              ) : null}

              {needsStockLeg ? (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    Your Stock Entry Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={stockEntryPrice}
                      onChange={(e) => setStockEntryPrice(parseFloat(e.target.value) || 0)}
                      className="w-full pl-7 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Enter your stock fill/cost basis</p>
                </div>
              ) : null}

              {/* Position Summary */}
              <div className="bg-slate-900/50 rounded-lg p-3 space-y-2 border border-slate-700">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Collateral Required</span>
                  <span className="text-white font-bold">${collateral.toLocaleString()}</span>
                </div>
                {!isShares ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Max Profit (Premium)</span>
                      <span className="text-green-400 font-bold">${maxProfit.toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Target Return</span>
                      <span className="text-cyan-400 font-bold">{targetReturn}%</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Position Notional</span>
                    <span className="text-cyan-400 font-bold">${collateral.toFixed(0)}</span>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-4 border-t border-slate-700">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 px-4 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || contracts < 1 || (!isShares && !entryPremium) || (needsStockLeg && stockBasis <= 0)}
                className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Opening...
                  </>
                ) : (
                  'Confirm Trade'
                )}
              </button>
            </div>
          </form>
        )}
    </Modal>
  );
};

export default OpenPositionModal;
